import { Component } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';
import queryString from 'query-string';
import { getPlaceFeedPage } from 'actions/feed';
import { toggleOnlyShowWorkOrders } from 'actions/ui';
import {
  selectPlaceFeedChunks,
  selectPlaceFeedChunksPagination,
} from 'selectors/feed';
import FeedChunk from 'components/FeedChunk';
import withInfiniteScroll from 'components/withInfiniteScroll';
import { ui, ButtonReset } from 'ui';
import { Tabs, Tab, TabNavLink } from 'ui/tabs';
import MDSpinner from 'react-md-spinner';
import { SERGIC_OFFER } from '../constants';
import { withTranslation } from 'react-i18next';
import { Box, Stack } from '@tymate/margaret';

const Title = styled.h2`
  margin: 0;
  padding: ${({ theme }) => theme.spacing(0.5)}
    ${({ theme }) => theme.spacing()};
  font-size: ${({ theme }) => theme.size(0)};
  border-bottom: 1px solid ${ui('separator')};
`;

class Feed extends Component {
  state = {
    isFetching: false,
  };

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps, prevState) {
    const { selectedPlaceId, scroll, onlyShowWorkOrders } = this.props;
    const { isFetching } = this.state;

    if (selectedPlaceId !== prevProps.selectedPlaceId) {
      this.fetchData();
    }

    if (onlyShowWorkOrders !== prevProps.onlyShowWorkOrders) {
      this.fetchData();
    }

    if (!isFetching && prevState.isFetching) {
      this.checkScrollPosition();
    }

    if (!isEqual(prevProps.scroll, scroll)) {
      this.checkScrollPosition();
    }
  }

  fetchData = async (page = 1) => {
    const {
      selectedPlaceId,
      getPlaceFeedPage,
      onlyShowWorkOrders,
    } = this.props;

    const search = onlyShowWorkOrders ? { by_kind: 'work_order' } : '';

    if (!selectedPlaceId) {
      return;
    }

    this.setState({ isFetching: true });

    await getPlaceFeedPage(
      selectedPlaceId,
      queryString.stringify({ ...search, page }),
    );

    this.setState({ isFetching: false });
  };

  checkScrollPosition = () => {
    const { scrollTop, innerHeight, offsetHeight } = this.props.scroll;
    const delta = 200;

    if (scrollTop + innerHeight + delta >= offsetHeight) {
      this.handleAboutToReachBottom();
    }
  };

  handleAboutToReachBottom = () => {
    const { pagination } = this.props;
    const { isFetching } = this.state;

    if (!pagination) {
      return;
    }

    const { page, lastPage } = pagination;

    if (page < lastPage && !isFetching) {
      this.fetchData(page + 1);
    }
  };

  render() {
    const { isFetching } = this.state;
    const {
      feedChunks,
      location,
      toggleOnlyShowWorkOrders,
      onlyShowWorkOrders,
      t,
    } = this.props;

    const readChunks = feedChunks.filter(({ unread }) => !unread);
    const unreadChunks = feedChunks.filter(({ unread }) => unread);

    return (
      <>
        {['VSY', 'DIR'].indexOf(SERGIC_OFFER) === -1 && (
          <Tabs style={{ paddingLeft: 16, marginBottom: 16 }}>
            <Tab>
              <TabNavLink
                as={ButtonReset}
                disabled={!onlyShowWorkOrders}
                onClick={toggleOnlyShowWorkOrders}
                className={!onlyShowWorkOrders && 'active'}
              >
                {t('all')}
              </TabNavLink>
            </Tab>
            <Tab>
              <TabNavLink
                as={ButtonReset}
                disabled={onlyShowWorkOrders}
                onClick={toggleOnlyShowWorkOrders}
                className={onlyShowWorkOrders && 'active'}
              >
                {t('ongoing')}
              </TabNavLink>
            </Tab>
          </Tabs>
        )}

        {isFetching && feedChunks.length === 0 && (
          <div style={{ padding: 16, paddingTop: 0, textAlign: 'center' }}>
            <MDSpinner singleColor={ui('primary')} />
          </div>
        )}

        {unreadChunks.length > 0 && (
          <>
            <Title>{t('activity')}</Title>

            <Box
              paddingTop={0.0625}
              paddingHorizontal={0}
              paddingBottom={1.5}
              style={{ backgroundColor: ui('background') }}
            >
              <Stack
                as="ul"
                direction="column"
                gutterSize={2}
                style={{ listStyleType: 'none' }}
                alignX="stretch"
                paddingLeft={0}
                paddingVertical={1}
              >
                {unreadChunks.map((feedChunk, key) => (
                  <FeedChunk
                    key={key}
                    isHighlighted={
                      Boolean(location?.state?.highlightedWorkOrder) &&
                      Number(location?.state?.highlightedWorkOrder) ===
                        feedChunk?.data?.id
                    }
                    {...feedChunk}
                  />
                ))}
              </Stack>
            </Box>
          </>
        )}

        {readChunks.length > 0 && (
          <>
            <Title>{t('history')}</Title>

            <Stack
              as="ul"
              direction="column"
              gutterSize={2}
              style={{ listStyleType: 'none' }}
              alignX="stretch"
              paddingLeft={0}
              paddingVertical={1}
            >
              {readChunks.map((feedChunk, key) => (
                <FeedChunk
                  key={key}
                  {...feedChunk}
                  isHighlighted={
                    Boolean(location?.state?.highlightedWorkOrder) &&
                    Number(location?.state?.highlightedWorkOrder) ===
                      feedChunk?.data?.id
                  }
                  displayName={
                    SERGIC_OFFER === 'VQC'
                      ? t(`notifications_type:${feedChunk.kind}`)
                      : feedChunk?.displayName
                  }
                />
              ))}
            </Stack>
          </>
        )}
      </>
    );
  }
}

const mapStateToProps = (state, props) => ({
  selectedPlaceId: state.ui.selectedPlaceId,
  feedChunks: selectPlaceFeedChunks(state, props),
  pagination: selectPlaceFeedChunksPagination(state, props),
  onlyShowWorkOrders: state.ui.onlyShowWorkOrders,
});

const mapDispatchToProps = dispatch => ({
  getPlaceFeedPage: (...args) => dispatch(getPlaceFeedPage(...args)),
  toggleOnlyShowWorkOrders: () => dispatch(toggleOnlyShowWorkOrders()),
});

export default withInfiniteScroll(
  connect(mapStateToProps, mapDispatchToProps)(withTranslation('feed')(Feed)),
);
